import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    AuthorityTypeDto,
    FEATURE_OUTSTANDING_TASKS_VIEW,
    PersonnelRegisterExpirySummary,
    PersonsOrganisationDto,
    PreferencesService,
    hasFeatureFlag
} from '@flyfreely-portal-ui/flyfreely';
import {
    ColumnSortPreferences,
    TableColumn,
    TableConfig
} from '@flyfreely-portal-ui/flyfreely-table';
import { FormatDatePipe } from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import {
    collapseOnLeaveAnimation,
    expandOnEnterAnimation
} from 'angular-animations';
import { AuthorityDialoguesService } from 'libs/authorities/src/lib/authority-dialogues.service';
import { applyPreferences } from 'libs/flyfreely-table/src/lib/helpers';
import { EMPTY, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import {
    OutstandingTasksViewTableSetupUserPreferences,
    WidgetRegistrationWithSection
} from '../../../outstanding-tasks-view.service';
import {
    AuthorityTaskWidgetsDataService,
    DisplayableRegisterExpiries,
    formatAuthorityExpiryWidgetStatuses
} from '../authority-data.service';

const REGISTER_EXPIRIES_WIDGET_IDENTIFIER = 'registerExpiriesTaskWidget';

@Component({
    selector: 'register-expiries-widget',
    templateUrl: './register-expiries-widget.component.html',
    styleUrls: ['../../widget-styles.scss'],
    animations: [expandOnEnterAnimation(), collapseOnLeaveAnimation()]
})
export class RegisterExpiriesWidgetComponent implements OnInit, OnDestroy {
    organisation: PersonsOrganisationDto;
    showBody = true;

    expiries: DisplayableRegisterExpiries[];

    //Static table variables
    availableColumns: TableColumn[];
    selectedColumns: string[];
    tableConfig: TableConfig;
    tableSorting: ColumnSortPreferences;
    userPreferences: OutstandingTasksViewTableSetupUserPreferences;

    formatStatuses = formatAuthorityExpiryWidgetStatuses;

    working = false;
    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private authorityDataService: AuthorityTaskWidgetsDataService,
        private workspaceStateService: WorkspaceStateService,
        private authorityDialoguesService: AuthorityDialoguesService,
        private formatDatePipe: FormatDatePipe,
        private preferencesService: PreferencesService
    ) {
        this.availableColumns = [
            {
                value: 'name',
                name: 'Description',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                linkFunction: row => this.viewRegister(row)
            },
            {
                value: 'type',
                name: 'Type',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                formatterFunction: (val, row) =>
                    val === 'PILOT' ? 'Pilot' : 'RPA'
            },
            {
                value: 'serial',
                name: 'Serial',
                searchable: false,
                selectable: true,
                defaultSelection: true
            },
            {
                value: 'authorityName',
                name: 'Authority',
                searchable: false,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'registerName',
                name: 'Register',
                searchable: false,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'expiryDate',
                name: 'Expiry Date',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                formatterFunction: d => this.formatDatePipe.transform(d)
            },
            {
                value: 'status',
                name: 'Status',
                searchable: false,
                selectable: false,
                defaultSelection: true
            }
        ];

        this.selectedColumns = null;
    }

    ngOnInit() {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.loadPreferences();
            });

        this.authorityDataService.working$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.authorityDataService.registerExpirySummaries$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(expiries => (this.expiries = expiries));
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    onToggleCollapse() {
        this.showBody = !this.showBody;
        this.userPreferences = {
            ...this.userPreferences,
            showBody: this.showBody
        };
        this.updateUserPreferences();
    }

    private loadPreferences() {
        if (!this.organisation) {
            return;
        }

        this.tableConfig = {
            timeZone: this.organisation.timeZone,
            limit: 3,
            actions: [],
            limitSelection: [3, 10, 25, 50, 100]
        };

        this.preferencesService
            .findPreferences<OutstandingTasksViewTableSetupUserPreferences>(
                'registerExpiriesWidgetTable',
                this.organisation.id
            )
            .pipe(
                catchError(() => {
                    this.handleDefaultPreferences();
                    return EMPTY;
                })
            )
            .subscribe(preferences => {
                if (preferences === null) {
                    this.handleDefaultPreferences();
                } else {
                    this.applyPreferences(preferences);
                }
            });
    }

    private handleDefaultPreferences() {
        this.tableConfig.limit = 3; // reset limit to default
        this.applyPreferences({});
    }

    private applyPreferences(
        preferences: OutstandingTasksViewTableSetupUserPreferences
    ) {
        this.userPreferences = applyPreferences(
            preferences,
            this.availableColumns,
            this.tableConfig
        );
        this.selectedColumns = this.userPreferences.selectedColumns;
        this.tableSorting = this.userPreferences.columnSorting;
        this.tableConfig.limit = this.userPreferences.itemLimit;
        this.showBody = this.userPreferences.showBody ?? true;
    }

    updateUserPreferences() {
        this.preferencesService
            .updatePreferences(
                'registerExpiriesWidgetTable',
                this.organisation.id,
                this.userPreferences
            )
            .subscribe();
    }

    viewRegister(register: DisplayableRegisterExpiries) {
        if (
            register.type ===
            PersonnelRegisterExpirySummary.RegisterEntityType.RPA
        ) {
            this.authorityDialoguesService.showRpaRegisterDialogue(
                this.organisation.id,
                <AuthorityTypeDto>register.authorityType,
                register.authority,
                register.authority.registers.find(
                    r => r.id === register.registerId
                ),
                this.organisation.id
            );
        } else {
            this.authorityDialoguesService.showPersonnelRegisterDialogue(
                this.organisation.id,
                <AuthorityTypeDto>register.authorityType,
                register.authority,
                register.authority.registers.find(
                    r => r.id === register.registerId
                ),
                this.organisation.id
            );
        }
    }

    onTablePageChanged(page: number) {
        this.tableConfig.currentPage = page;
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;
        if (
            this.tableSorting != null &&
            !this.selectedColumns.includes(this.tableSorting.column)
        ) {
            this.tableSorting = null;
        }
        this.userPreferences = {
            ...this.userPreferences,
            selectedColumns: selectedColumns,
            columnSorting: this.tableSorting || null
        };
        this.updateUserPreferences();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.tableSorting =
            sorting == null
                ? null
                : {
                      column: sorting.column.toString(),
                      ascending: sorting.ascending
                  };
        this.tableConfig.currentPage = 0;
        this.userPreferences = {
            ...this.userPreferences,
            columnSorting: sorting
        };
        this.updateUserPreferences();
    }

    updateItemLimit(limit: number) {
        this.tableConfig.limit = limit;
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        this.updateUserPreferences();
    }
}

export const registerExpiriesWidgetRegistration: WidgetRegistrationWithSection =
    {
        widgetIdentifier: REGISTER_EXPIRIES_WIDGET_IDENTIFIER,
        section: 'organisation',
        component: RegisterExpiriesWidgetComponent,
        isAvailable: organisation =>
            hasFeatureFlag(organisation, FEATURE_OUTSTANDING_TASKS_VIEW)
    };
